import './styles.scss'

import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Tooltip from 'rc-tooltip'
import { useSelector } from 'react-redux'

import I18n from 'lib/i18n'
import Price from 'lib/tags/price'
import Twicpics from 'lib/tags/twicpics'
import distanceFromLatLonInKm from 'lib/utils/geo/distance-from-lat-lon-in-km'
import railsContext from 'lib/selectors/rails-context'

import Rating from '~/scenes/shared/rating'

import Reputation from './reputation'

const Result = ({
  center,
  dpr,
  hoverResult,
  isFavorite,
  isHovering,
  locale,
  removeFavorite,
  result,
  score,
  setFavorite,
  settingFavorite,
  showFullLocation,
  translateResultRequest
}) => {
  const {
    featureFlags: { isAdminUser }
  } = useSelector(railsContext)

  const [favoriteChanged, setFavoriteChanged] = useState(false)

  useEffect(() => {
    setFavoriteChanged(true)
  }, [isFavorite])

  const handleMouseEnter = useCallback(() => {
    hoverResult(result)
  }, [hoverResult, result])

  const handleMouseLeave = useCallback(() => {
    hoverResult(null)
  }, [hoverResult])

  const handleToggleFavorite = useCallback(
    event => {
      event.preventDefault()

      if (isFavorite) {
        removeFavorite(result)
      } else {
        setFavorite(result)
      }
    },
    [isFavorite, removeFavorite, setFavorite, result]
  )

  const handleTranslate = useCallback(
    event => {
      event.preventDefault()

      translateResultRequest(result.id, result.translated ? null : locale)
    },
    [result, locale, translateResultRequest]
  )

  const showTranslate = result.language !== locale || result.translated
  const path = `${result.path}?from_landing_page=true`

  let fullLocationString = result.location_string
  let minDistance = null

  result.locations.forEach(location => {
    const distance = center ? distanceFromLatLonInKm(location.latitude, location.longitude, center[0], center[1]) : null

    if (distance && (minDistance === null || distance < minDistance)) {
      minDistance = distance
      fullLocationString = location.long_city
    }
  })

  const locationString = fullLocationString ? fullLocationString.replace(/,[^,]+$/, '').trim() : null

  const tags = []
  let indexKey = 0
  result.tags.forEach(tag => {
    if (tags.length > 0) {
      tags.push(<span key={indexKey++}> · </span>)
    }
    tags.push(<span key={indexKey++}>{tag}</span>)
  })

  return (
    <div
      id={`result-${result.id}`}
      className={`search-result${isHovering ? ' hover' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className='result-content'>
        <div className='image' onClick={() => window.open(path, '_blank')}>
          {result.user_video_url && (
            <div className='play-btn-container'>
              <div className='play-btn' />
            </div>
          )}
          <Twicpics src={result.avatar} width={150} alt={result.title} dpr={dpr} lazyLoad />
          {result.verified && <Reputation id={result.user_id} />}
        </div>

        <div className='responsive-image'>
          <a href={path} target='_blank' aria-label={result.title} rel='noreferrer'>
            <Twicpics src={result.avatar} width={70} alt={result.title} dpr={dpr} />
            {result.verified && <Reputation id={result.user_id} />}
          </a>
        </div>

        <div className='text'>
          <div className={`name-rating-row${result.boosted ? ' name-rating-row-boosted-sbp' : ''}`}>
            {!!result.rating && (
              <div className='right'>
                <Rating rating={result.rating / 20.0} dpr={dpr} />
                <span className='num-reviews'>{I18n.t('react.search.result.reviews', { count: result.reviews })}</span>
              </div>
            )}
            <a href={path} className='name' target='_blank' rel='noreferrer'>
              <span>
                {result.name}
                {locationString && (
                  <span className='user-location'> - {showFullLocation ? fullLocationString : locationString}</span>
                )}
              </span>

              {result.boosted && (
                <span className='boosted-course-tag'>{I18n.t('react.search.result.recently_active')}</span>
              )}
            </a>
            <span className='name-rating-price'>
              <Price currency={result.currency} price={Math.round(result.price_number)} />
            </span>
          </div>

          <div className='title-price-row'>
            <div className='title-price-inner'>
              <span className='price'>
                <Price currency={result.currency} price={Math.round(result.price_number)} />
              </span>
              <a className='title-link' href={path} target='_blank' rel='noreferrer'>
                <span className='title'>{result.title}</span>
              </a>
            </div>
          </div>
          <div className='result-content'>
            <a href={path} target='_blank' rel='noreferrer'>
              {result.premium ? (
                <span className='premium-teacher'>{I18n.t('react.search.result.premium_teacher')} </span>
              ) : (
                ''
              )}
              <span>{result.description}</span>
            </a>
          </div>
          {/* Hide tags temporarily (visibility hidden) */}
          {/* refer to https://app.asana.com/1/745456032100/task/1211601735353800/comment/1211970262636649?focus=true for more details */}
          <div className='result-tags'>{tags}</div>
          {isAdminUser && <div className='user-ranking'>score: {score}</div>}
        </div>
        <div className='props'>
          <div
            className={`heart${favoriteChanged ? ' changed' : ''}${isFavorite ? ' active' : ' inactive'}`}
            style={{ cursor: settingFavorite ? 'wait' : 'pointer' }}
            onClick={handleToggleFavorite}
          />
          {showTranslate && (
            <Tooltip
              key={result.translated ? 'translated-tooltip' : 'translate-tooltip'}
              placement='left'
              trigger={['hover']}
              overlay={
                result.translated
                  ? I18n.t('jstemplates.searchresult.translated')
                  : I18n.t('jstemplates.searchresult.translate')
              }
            >
              <a className='translate' href='#' onClick={handleTranslate}>
                {I18n.t('jstemplates.searchresult.translate')}
              </a>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

Result.propTypes = {
  result: PropTypes.object.isRequired,
  center: PropTypes.array,
  isFavorite: PropTypes.bool,
  isHovering: PropTypes.bool,
  settingFavorite: PropTypes.bool,
  showFullLocation: PropTypes.bool,
  locale: PropTypes.string,
  dpr: PropTypes.number,
  score: PropTypes.number,
  hoverResult: PropTypes.func,
  setFavorite: PropTypes.func,
  removeFavorite: PropTypes.func,
  translateResultRequest: PropTypes.func
}

export default Result
